import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const EventQuestionsForm = () => {
    // Form state
    const { eventId } = useParams();
    const [questions, setQuestions] = useState([
        { question: '', ordinal: 1 }
    ]);

    // UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success', 'error', or 'info'

    const navigate = useNavigate();

    // Load questions on component mount or when eventId changes
    useEffect(() => {
        if (eventId) {
            fetchEventQuestions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    // Fetch questions from the API
    const fetchEventQuestions = async () => {
        setLoading(true);
        setMessage('');

        try {
            const response = await axios.get(`/otgservices/getEventQuestions.php?event_id=${eventId}`);

            if (response.data.success && response.data.questions.length > 0) {
                // Sort questions by ordinal
                const sortedQuestions = response.data.questions
                    .sort((a, b) => a.ordinal - b.ordinal)
                    .map(q => ({ question: q.question, ordinal: q.ordinal }));

                setQuestions(sortedQuestions);
                setMessage('Questions loaded successfully.');
                setMessageType('success');
            } else {
                // No questions found, start with one empty question
                setQuestions([{ question: '', ordinal: 1 }]);
                setMessage('No existing questions found. You can add questions below.');
                setMessageType('info');
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            setMessage('Failed to load questions. Please try again later.');
            setMessageType('error');
            // Start with one empty question
            setQuestions([{ question: '', ordinal: 1 }]);
        } finally {
            setLoading(false);
        }
    };

    // Handle input changes
    const handleQuestionChange = (index, value) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index] = {
            ...updatedQuestions[index],
            question: value
        };
        setQuestions(updatedQuestions);
    };

    // Add a new question
    const addQuestion = () => {
        if (questions.length >= 3) {
            setMessage('Maximum of 3 questions allowed.');
            setMessageType('error');
            return;
        }

        const newOrdinal = questions.length + 1;
        setQuestions([...questions, { question: '', ordinal: newOrdinal }]);
    };

    // Remove a question
    const removeQuestion = (index) => {
        if (questions.length <= 1) {
            setMessage('At least one question is required.');
            setMessageType('error');
            return;
        }

        const updatedQuestions = questions.filter((_, i) => i !== index);

        // Update ordinals after removal
        const reorderedQuestions = updatedQuestions.map((q, i) => ({
            ...q,
            ordinal: i + 1
        }));

        setQuestions(reorderedQuestions);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');

        // Filter out empty questions
        const questionsToSave = questions
            .filter(q => q.question.trim() !== '')
            .map((q, index) => ({ ...q, ordinal: index + 1 })); // Ensure ordinals are sequential

        if (questionsToSave.length === 0) {
            setMessage('Please enter at least one question before saving.');
            setMessageType('error');
            setSaving(false);
            return;
        }

        try {
            const payload = {
                eventId: parseInt(eventId),
                questions: questionsToSave
            };

            const response = await axios.post('/otgservices/InsertOrUpdateEventQuestions.php', payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setMessage(`Questions saved successfully! (${response.data.inserted} inserted, ${response.data.updated} updated)`);
                setMessageType('success');

                // Refresh questions from server to ensure we have the latest data
                fetchEventQuestions();
            } else {
                setMessage(`Failed to save questions: ${response.data.message}`);
                setMessageType('error');
            }
        } catch (error) {
            console.error('Error saving questions:', error);
            setMessage('Failed to save questions. Please try again later.');
            setMessageType('error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 px-4 py-6">
            {/* Container with responsive width - 3/4 on large screens, full width on mobile */}
            <div className="mx-auto w-full md:w-3/4 max-w-4xl">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-2xl font-bold mb-4">Event Registration Questions</h2>
                    <p className="mb-4 text-gray-600">
                        Configure up to 3 questions that participants will answer when registering for this event.
                    </p>
                    {message && (
                        <div className={`p-4 mb-4 rounded-md ${
                            messageType === 'success' ? 'bg-green-100 text-green-700' :
                                messageType === 'error' ? 'bg-red-100 text-red-700' :
                                    'bg-blue-100 text-blue-700'
                        }`}>
                            {message}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {questions.map((q, index) => (
                                <div key={index} className="space-y-2 bg-gray-50 p-4 rounded-md relative">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Question {index + 1}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => removeQuestion(index)}
                                            className="text-red-500 hover:text-red-700 focus:outline-none"
                                            title="Remove question"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20"
                                                 fill="currentColor">
                                                <path fillRule="evenodd"
                                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                      clipRule="evenodd"/>
                                            </svg>
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        value={q.question}
                                        onChange={(e) => handleQuestionChange(index, e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        placeholder={`Enter question ${index + 1}`}
                                    />
                                </div>
                            ))}

                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={addQuestion}
                                    disabled={questions.length >= 3}
                                    className={`px-4 py-2 text-gray-700 bg-gray-200 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                                        questions.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                >
                                    <span className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20"
                                             fill="currentColor">
                                            <path fillRule="evenodd"
                                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                  clipRule="evenodd"/>
                                        </svg>
                                        Add Question ({questions.length}/3)
                                    </span>
                                </button>
                            </div>

                            <div className="pt-4 border-t border-gray-200">
                                <div className="flex justify-between"> {/* This ensures the buttons are at opposite ends */}
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                                    >
                                        Back to Event
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className={`px-4 py-2 text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                            saving ? 'opacity-75 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        {saving ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                                                     fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                            strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor"
                                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Saving...
                                            </span>
                                        ) : 'Save Questions'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventQuestionsForm;